import { CORE_API_HOSTNAME } from "../../env";
import { expiresAt, getDecodedJwt, validToken } from "../../helpers/jwt";
import { refresh } from "../auth";
import { fetchAccountToken, fetchExchangeToken } from "../dinteroAuthApi";
import type { CognitoSession, PartnerUser } from "../types";
import { getAccountIdFromAudience } from "./helpers";

export interface ExchangeRefreshResult {
    /** A freshly minted exchange access token for the inspected account. */
    accessToken: string;
    /**
     * Present only when the partner cognito session had to be refreshed, so the
     * caller can persist the refreshed cognito tokens. Carries refreshed cognito
     * fields only — callers that need a full PartnerUser must merge it onto the
     * partner they already hold.
     */
    partnerSession?: CognitoSession;
}

/**
 * Determine whether `token` is an exchange token minted for an inspect session.
 * Requires a stored partner session, since only a partner can refresh it.
 */
export const isExchangeToken = (
    token: string | undefined,
    partnerSession: PartnerUser | undefined,
): boolean => {
    if (!token || !partnerSession) {
        return false;
    }
    const { iss, exchanged, aud } = getDecodedJwt(token);
    return Boolean(aud && iss === CORE_API_HOSTNAME && exchanged);
};

/** The exchange token's `exp` claim, in seconds since epoch. */
export const exchangeTokenExpiry = (token: string): number =>
    Math.floor(expiresAt(token).getTime() / 1000);

/**
 * Mint an exchange token for `inspectedAccountId` acting as `partnerId`,
 * refreshing the partner cognito session first if its access token has expired.
 * Returns undefined if the partner cannot be refreshed or the exchange fails.
 */
export const mintExchangeToken = async (
    partnerId: string,
    inspectedAccountId: string,
    partnerSession: CognitoSession,
): Promise<ExchangeRefreshResult | undefined> => {
    let partnerCognitoToken = validToken(partnerSession.cognito_access_token);
    let refreshedPartnerSession: CognitoSession | undefined;
    if (!partnerCognitoToken) {
        const updated = await refresh(partnerSession);
        if (updated) {
            refreshedPartnerSession = { ...partnerSession, ...updated };
            partnerCognitoToken = updated.cognito_access_token;
        }
    }
    if (!partnerCognitoToken) {
        return undefined;
    }

    // The exchange_token endpoint authenticates with the partner's *account*
    // token (account-scoped, admin:accounts) — not the cognito identity token.
    // Mint it from the partner cognito token first, then exchange with it.
    const partnerAccount = await fetchAccountToken(
        partnerCognitoToken,
        partnerId,
    );
    if (!partnerAccount) {
        return undefined;
    }

    const exchangeToken = await fetchExchangeToken(
        partnerId,
        inspectedAccountId,
        partnerAccount.token,
    );
    if (!exchangeToken) {
        return undefined;
    }

    return {
        accessToken: exchangeToken.access_token,
        partnerSession: refreshedPartnerSession,
    };
};

/**
 * Re-mint an (expired) exchange token using the partner session it was derived
 * from. Refreshes the partner cognito token first when necessary.
 *
 * Returns `undefined` when the partner session cannot be refreshed or the
 * exchange request fails. Callers must NOT fall back to refreshing the expired
 * exchange token as if it were a regular cognito session — its refresh token
 * and config belong to the partner, not the inspected account.
 */
export const refreshExchangeToken = async (
    expiredExchangeToken: string,
    partnerSession: CognitoSession,
): Promise<ExchangeRefreshResult | undefined> => {
    const { exchanged, aud } = getDecodedJwt(expiredExchangeToken);
    if (!exchanged || !aud) {
        return undefined;
    }
    // exchanged.account_id is the partner (URL) account; the `aud` username is
    // the inspected account — mirrors the original inspect exchange request.
    return mintExchangeToken(
        exchanged.account_id,
        getAccountIdFromAudience(aud),
        partnerSession,
    );
};
